import { cn } from '@/lib/utils';
import { ONBOARDING_STEPS } from './flow.helpers';

interface OnboardingStepperProps {
  currentStep: number;
  isDone: (step: number) => boolean;
  isDeferred: (step: number) => boolean;
}

/** The step rail: current step filled, done steps ✓, deferred steps ·.
 * Bubbles show DISPLAY positions (index + 1) — `step.n` is the wire position,
 * which skips the retired Recorrentes slot (see flow.helpers). */
export function OnboardingStepper({ currentStep, isDone, isDeferred }: OnboardingStepperProps) {
  return (
    <nav aria-label="Etapas" className="mb-7">
      <ol className="flex items-center gap-1.5">
        {ONBOARDING_STEPS.map((step, index) => {
          const current = step.n === currentStep;
          const done = isDone(step.n);
          const deferred = isDeferred(step.n);
          const last = index === ONBOARDING_STEPS.length - 1;
          return (
            <li
              key={step.n}
              aria-current={current ? 'step' : undefined}
              className={cn('flex min-w-0 items-center gap-1.5', !last && 'flex-1')}
            >
              <span
                aria-hidden
                className={cn(
                  'grid size-6 shrink-0 place-items-center rounded-full font-mono text-[11px] font-bold',
                  current
                    ? 'bg-primary text-primary-foreground'
                    : done
                      ? 'bg-brand-50 text-primary'
                      : 'bg-surface-sunken text-text-tertiary',
                )}
              >
                {deferred ? '·' : done && !current ? '✓' : index + 1}
              </span>
              <span
                // Non-current labels are aria-hidden even where visible (sm+):
                // the sr-only node below is their single name+state
                // announcement — exposing both reads "Validação, Validação:
                // concluída".
                aria-hidden={!current}
                className={cn(
                  'min-w-0 truncate text-[11px] font-semibold',
                  // At phone widths every label truncates to "Cone… Cate…" —
                  // keep only the current step's label; the others read by number/✓.
                  current ? 'shrink-0 text-text-primary' : 'hidden text-text-tertiary sm:block',
                )}
              >
                {step.label}
              </span>
              {!current && (
                <span className="sr-only">
                  {`${step.label}: ${deferred ? 'adiada' : done ? 'concluída' : 'pendente'}`}
                </span>
              )}
              {!last && <span aria-hidden className="h-px min-w-2 flex-1 bg-border" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
